// transcript-timing.util.ts — pure timing math for the synced transcript player.
//
// Two non-obvious facts everything here rests on:
//
// 1. A turn's `ts` is stamped when its TEXT lands in the buffer, and the two sides land
//    differently: human turns finalize after STT — `ts` ≈ end of the utterance — while AI
//    turns stamp when the model finishes GENERATING, which can be near the start of the
//    spoken audio (the TTS plays it out for many more seconds). No single anchor rule fits
//    both, so when the recorder's measured who-spoke-when segments exist they own the turn
//    windows outright and `ts` is ignored; only segment-less turns fall back to a ts-anchored
//    estimate (~0.38 s/word before the stamp).
//
// 2. `who` on agent turns is the product's display name (Alex, AVA, Maya…), and older
//    session rows may have no aiAgentName to match it against. Human turns are the ones
//    carrying `speaker`/`pid` (participant metadata) — so an unrecognized bare name with no
//    participant metadata is the agent. Matching a name list alone is what kept putting
//    AVA on the prospect side.
//
// Word times are interpolated across the turn window (char-weighted) — estimated, not
// measured; there is no per-word STT timing anywhere in the system.
import { parseApiDate } from './tracking-format.util';
import type { ReportRecording, SessionReport } from './tracking-demo.models';

export type ReportTurn = SessionReport['transcript'][number];

export interface TimedWord {
  w: string;
  /** Seconds on the audio clock where this word is (estimated to) start. */
  at: number;
}

/** One transcript turn with everything the player renders precomputed. */
export interface TimedTurn {
  turn: ReportTurn;
  ai: boolean;
  name: string;
  /** Turn window in seconds on the audio clock; null when the turn has no `ts`. */
  start: number | null;
  end: number | null;
  words: TimedWord[] | null;
  /** True when the window came from a measured speech segment, not a ts estimate. */
  measured: boolean;
  /** The turn's own stamp on the audio clock, seconds (null without a ts). */
  tsSec: number | null;
  /** Wall-clock HH:MM:SS of the turn. */
  wall: string;
  /** "m:ss" offset of the turn's start into the audio. */
  offset: string;
  /** "m:ss" spoken length of the turn. */
  dur: string;
}

export interface TalkStat {
  name: string;
  ai: boolean;
  sec: number;
  /** Share of the call duration, 0–100 (rounded). */
  pct: number;
}

export interface TimelineLane {
  name: string;
  ai: boolean;
  /** `at`/`end` in seconds on the audio clock. */
  segs: Array<{ leftPct: number; widthPct: number; at: number; end: number }>;
}

export { rulerTicks, segmentAt } from './timeline-cursor.util';
export type { RulerTick, SpeakingNow } from './timeline-cursor.util';

/** Which file the player is driving: the conversation, or one side of it. */
export type PlaybackMode = 'mixed' | 'human' | 'agent';

/** Known speaker names per side, lowercased (the seed for the structural rule below). */
export interface SpeakerSides {
  ai: Set<string>;
  human: Set<string>;
}

/** Everything the transcript player renders, computed in one pass. */
export interface TranscriptModel {
  rows: TimedTurn[];
  stats: TalkStat[];
  deadAirSec: number;
  lanes: TimelineLane[];
  durationSec: number;
}

/** Estimate fallback: assume ~this much speech per word before the turn's end stamp. */
const SEC_PER_WORD = 0.38;
const MIN_TURN_SEC = 1.2;
/** Words never run right up to the window edge — the finalize pipeline eats a beat. */
const TAIL_SEC = 0.2;
/** Never absorb a follow-on segment across a silence longer than this. */
const ABSORB_MAX_GAP_SEC = 30;

const norm = (s: string | null | undefined): string => (s || '').trim().toLowerCase();

export function speakerSides(report: SessionReport): SpeakerSides {
  const ai = new Set(['alex', 'ai', 'agent']);
  const human = new Set(['prospect']);
  if (report.session?.aiAgentName) ai.add(norm(report.session.aiAgentName));
  if (report.session?.hostName) human.add(norm(report.session.hostName));
  for (const p of report.participants || []) {
    if (!p.name) continue;
    (p.role === 'ai' ? ai : human).add(norm(p.name));
  }
  return { ai, human };
}

/** AI side or not, structurally: a known AI name is the agent; participant metadata
 *  (`speaker`/`pid`), a known human name, or the legacy 'Prospect' literal is a human;
 *  an unrecognized bare name with none of that is the agent under a name we weren't
 *  told (the AVA case — agent turns never carry participant metadata). */
export function isAiTurn(
  turn: Pick<ReportTurn, 'who' | 'speaker' | 'pid'>, sides: SpeakerSides): boolean {
  const who = norm(turn.who);
  if (sides.ai.has(who)) return true;
  if (turn.pid || turn.speaker) return false;
  if (!who || sides.human.has(who)) return false;
  return true;
}

/** Ordered candidate audio sources: the both-speaker mix first, then the rendered
 *  mp4 / raw webm / public url. A URL being present here only means it was UPLOADED
 *  — not that it actually decodes (a beheaded webm upload is exactly that case) — so
 *  the player tries them in order at runtime (its (error) handler) rather than this
 *  function silently picking the first one and hoping. */
export function audioSrcCandidatesOf(rec: ReportRecording | null | undefined): string[] {
  return [rec?.audioMixUrl, rec?.videoRenderUrl, rec?.videoUrl, rec?.publicUrl]
    .filter((u): u is string => !!u);
}

/** Which sides the listener switched on. */
export interface PlaybackSides { human: boolean; agent: boolean; }

/** Both sides on (or none — silence is never the answer) → the mix; one side → that
 *  side's own file. */
export function playbackModeFor(sides: PlaybackSides): PlaybackMode {
  if (sides.human === sides.agent) return 'mixed';
  return sides.human ? 'human' : 'agent';
}

/** Candidate sources for one playback mode. Mixed keeps the fallback chain; a
 *  single side has exactly one file (empty when that leg was never built), all
 *  three sample-locked on the session clock so a mode switch keeps currentTime. */
export function audioSourceFor(rec: ReportRecording | null | undefined, mode: PlaybackMode): string[] {
  // The small opus leg first; the raw WAV every finalize stores as the fallback.
  const legs = mode === 'human' ? [rec?.audioHumanUrl, rec?.audioUserUrl]
    : mode === 'agent' ? [rec?.audioAgentUrl, rec?.audioBotUrl] : null;
  if (legs) return legs.filter((u): u is string => !!u);
  return audioSrcCandidatesOf(rec);
}

/** The transcript player's audio source: the both-speaker mix, else the rendered mp4 /
 *  raw webm — an <audio> element plays just their audio track, on the same clock. */
export function audioSrcOf(rec: ReportRecording | null | undefined): string | null {
  return audioSrcCandidatesOf(rec)[0] ?? null;
}

export function mmss(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

/** Absolute epoch-ms the audio starts at. */
function audioT0Ms(report: SessionReport): number | null {
  const rec = report.recording;
  return parseApiDate(rec?.audioStartTime ?? rec?.startTime)?.getTime()
    ?? (report.transcript || [])
      .map((t) => parseApiDate(t.ts)?.getTime())
      .find((ms) => ms != null)
    ?? null;
}

interface Span { name: string; ai: boolean; s: number; e: number; used?: boolean; }

/** Speaker segments rebased from the recorder's clock onto the audio clock, in seconds. */
function rebasedSegments(rec: ReportRecording | null | undefined,
  aiSet: Set<string>, t0: number): Span[] {
  const laneT0 = parseApiDate(rec?.speakerTimelineStart)?.getTime() ?? t0;
  const delta = (laneT0 - t0) / 1000;
  return (rec?.speakerTimeline || [])
    .filter((g) => g && g.t1 > g.t0)
    .map((g) => ({
      name: g.label,
      // Host-tab rows say which side they are; recorder-page rows are named only.
      ai: g.type ? g.type === 'agent' : aiSet.has(norm(g.label)),
      s: delta + g.t0 / 1000,
      e: delta + g.t1 / 1000,
    }))
    .sort((a, b) => a.s - b.s);
}

const hhmmss = (d: Date): string =>
  d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });

/** One pass over the report: classify sides, window every turn, derive stats + lanes. */
export function buildTranscriptModel(report: SessionReport): TranscriptModel {
  const sides = speakerSides(report);
  const turns = report.transcript || [];
  const flags = turns.map((t) => isAiTurn(t, sides));
  // Segment labels are bare display names — enrich the AI set with the names the
  // structural rule just classified, so an untold agent name still labels its lane.
  const aiSet = new Set(sides.ai);
  turns.forEach((t, i) => { if (flags[i]) aiSet.add(norm(t.who)); });

  const t0 = audioT0Ms(report);
  const segs: Span[] = t0 != null ? rebasedSegments(report.recording, aiSet, t0) : [];

  // Per-side chronological assignment: turn k of a side takes that side's next unused
  // segment(s) — independent of `ts`, which is unreliable on AI turns (see header).
  const turnsLeft = { ai: flags.filter(Boolean).length, human: flags.length - flags.filter(Boolean).length };
  const nextOf = (ai: boolean) => segs.find((g) => !g.used && g.ai === ai);
  let prevEnd = 0;
  const rows: TimedTurn[] = turns.map((turn, i) => {
    const ai = flags[i];
    const name = ai
      ? (turn.who || 'AI')
      : (turn.speaker || turn.who || report.session?.hostName
        || report.session?.companyName || 'Prospect');
    const d = parseApiDate(turn.ts);
    const side = ai ? 'ai' : 'human';
    turnsLeft[side] -= 1;

    let start: number | null = null;
    let end: number | null = null;
    const seg = nextOf(ai);
    if (seg) {
      seg.used = true;
      start = seg.s;
      end = seg.e;
      // Absorb VAD-split follow-ons: only the extras beyond one-per-remaining-turn,
      // never across the other side's next utterance (that's their turn coming up),
      // never across a long silence.
      for (let cand = nextOf(ai); cand; cand = nextOf(ai)) {
        const other = nextOf(!ai);
        const extras = segs.filter((g) => !g.used && g.ai === ai).length - turnsLeft[side];
        if (extras <= 0 || (other && other.s < cand.s) || cand.s - end > ABSORB_MAX_GAP_SEC) break;
        cand.used = true;
        end = cand.e;
      }
    } else if (t0 != null && d) {
      // No measured segment left for this side — ts-anchored estimate.
      end = Math.max(0, (d.getTime() - t0) / 1000);
      const nWords = (turn.text || '').split(/\s+/).filter(Boolean).length;
      start = Math.max(prevEnd, end - Math.max(MIN_TURN_SEC, nWords * SEC_PER_WORD));
      if (start > end) start = end;
    }
    if (end != null) prevEnd = Math.max(prevEnd, end);

    // Char-weighted linear spread — long words get more time than "a" or "to".
    let words: TimedWord[] | null = null;
    if (start != null && end != null) {
      const raw = (turn.text || '').split(/\s+/).filter(Boolean);
      const total = raw.reduce((n, w) => n + w.length + 1, 0) || 1;
      const span = Math.max(0, end - TAIL_SEC - start);
      let cum = 0;
      words = raw.map((w) => {
        const at = start! + (cum / total) * span;
        cum += w.length + 1;
        return { w, at };
      });
    }
    return {
      turn, ai, name, start, end, words,
      measured: !!seg,
      tsSec: d && t0 != null ? (d.getTime() - t0) / 1000 : null,
      wall: d ? hhmmss(d) : '',
      offset: start != null ? mmss(start) : '',
      dur: start != null && end != null ? mmss(end - start) : '',
    };
  });

  // Stats + lanes from the measured segments when present, turn windows otherwise.
  const spans: Span[] = segs.length ? segs : rows
    .filter((r) => r.start != null && r.end != null && r.end > r.start)
    .map((r) => ({ name: r.name, ai: r.ai, s: r.start!, e: r.end! }));
  const durationSec = report.recording?.durationMs
    ? report.recording.durationMs / 1000
    : spans.reduce((n, g) => Math.max(n, g.e), 0);
  const dur = durationSec || 1;

  const byName = new Map<string, Span[]>();
  for (const g of spans) byName.set(g.name, [...(byName.get(g.name) || []), g]);
  const stats: TalkStat[] = [...byName.entries()]
    .map(([name, gs]) => ({
      name, ai: gs[0].ai, sec: mergedSec(gs),
      pct: Math.round((mergedSec(gs) / dur) * 100),
    }))
    .sort((a, b) => Number(b.ai) - Number(a.ai) || b.sec - a.sec);
  const lanes: TimelineLane[] = [...byName.entries()]
    .map(([name, gs]) => ({
      name, ai: gs[0].ai,
      segs: gs.map((g) => ({
        leftPct: Math.min(100, (g.s / dur) * 100),
        widthPct: Math.max(0.5, Math.min(100, ((g.e - g.s) / dur) * 100)),
        at: g.s,
        end: g.e,
      })),
    }))
    .sort((a, b) => Number(b.ai) - Number(a.ai));

  return {
    rows, stats, lanes, durationSec,
    deadAirSec: Math.max(0, dur - mergedSec(spans)),
  };
}

/** Sum of merged intervals — overlaps counted once. */
function mergedSec(spans: Span[]): number {
  let sec = 0;
  let cur: { s: number; e: number } | null = null;
  for (const g of [...spans].sort((a, b) => a.s - b.s)) {
    if (cur && g.s <= cur.e) {
      cur.e = Math.max(cur.e, g.e);
    } else {
      if (cur) sec += cur.e - cur.s;
      cur = { s: g.s, e: g.e };
    }
  }
  if (cur) sec += cur.e - cur.s;
  return sec;
}
